import {
  AlarmClockIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowUpIcon,
  ChevronRightIcon,
  ClockIcon,
  FileTextIcon,
  GlobeIcon,
  HistoryIcon,
  ListIcon,
  Loader2Icon,
  MicIcon,
  MonitorIcon,
  PanelRightIcon,
  MessagesSquareIcon,
  MousePointerClickIcon,
  PhoneIcon,
  PlayIcon,
  PlusIcon,
  RotateCwIcon,
  RocketIcon,
  SearchIcon,
  ShieldCheckIcon,
  SunIcon,
  TerminalIcon,
  Trash2Icon,
  TriangleAlertIcon,
  WrenchIcon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";

import { useMorrowSignal } from "../../hooks/useMorrowSignal";
import {
  agentBrowserLive,
  browserUseStatus,
  cachedAgentRuns,
  cachedAgents,
  deleteAgent,
  deleteAgentTag,
  fetchAgentRuns,
  fetchAgents,
  invalidateAgents,
  openBrowserUse,
  runAgent,
  runAgentCommand,
  setAgentKeepWorking,
  setAgentStatus,
  stopBrowserUse,
  updateAgent,
  upsertAgentTag,
  type Agent,
  type AgentAutonomy,
  type AgentRun,
} from "../../morrowAgents";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import ChatMarkdown from "../ChatMarkdown";
import {
  AgentTabs,
  CAP_KEYS,
  joinCharter,
  splitCharter,
  useConnectedApps,
  type AgentFormValues,
} from "./agentFormBits";
import { SidebarInset, useSidebar } from "../ui/sidebar";
import { AgentAvatar, isImage, MascotSvg } from "./AgentAvatar";
import { agentPalette, hueFrom, presetMascotBody, type AgentPalette } from "./agentHue";
import {
  useVoiceConversation,
  visibleMessageText,
  voiceReplyFrom,
  voiceTurnText,
  type VoiceReply,
} from "../voice/useVoiceConversation";
import { parseAgentWake, type AgentWake } from "./agentWake";
import { VoiceOrb } from "../voice/VoiceOrb";
import { VoicePanel } from "../voice/VoicePanel";
import { useVoiceChoice, VOICE_OPTIONS } from "../voice/voices";
import { resolveContextToolIcon } from "../context/contextToolIcon";
import {
  useThreadActivities,
  useThreadMessages,
  useThreadRefs,
  useThreadSession,
} from "~/state/entities";
import type { OrchestrationThreadActivity } from "@t3tools/contracts";
import { useComposerDictation } from "../chat/useComposerDictation";
import { joinPromptAndTranscript } from "../chat/composerDictation";
import { threadEnvironment } from "~/state/threads";
import { useAtomCommand } from "~/state/use-atom-command";
import { isAtomCommandInterrupted } from "@t3tools/client-runtime/state/runtime";
import { newMessageId, newVoiceMessageId } from "~/lib/utils";
import { MORROW_AGENT_MODEL_SELECTION } from "~/providerInstances";

const INPUT =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground " +
  "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

// Round floating buttons over the chat (back, call, computer) — card surface, hairline ring.
const FLOAT_BUTTON =
  "pointer-events-auto flex size-9 items-center justify-center rounded-full border border-border " +
  "bg-card text-muted-foreground shadow-sm transition hover:bg-accent hover:text-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

// Quiet icon buttons inside the composer pill (+, mic).
const COMPOSER_ICON_BUTTON =
  "flex size-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition " +
  "hover:bg-accent hover:text-foreground disabled:opacity-40";

/** "Scout — QA" → "Scout": the name people call the agent; the role shows separately. */
function agentShortName(name: string): string {
  return name.split(/\s+[—–-]\s+/)[0]?.trim() || name;
}

/** The agent's character at full shape (never cropped into a circle). Preset characters are
 * drawn as SVG in the agent's colour; emoji / custom images fall back to the round avatar. */
function AgentMascot({
  agent,
  size,
  thinking = false,
}: {
  agent: Agent;
  size: number;
  thinking?: boolean;
}) {
  const body = agent.avatar ? presetMascotBody(agent.avatar) : null;
  if (body) return <MascotSvg color={body} size={size} thinking={thinking} glow={size >= 40} />;
  const src = agent.avatar?.trim() ?? "";
  if (src && isImage(src)) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden
        draggable={false}
        className="block select-none rounded-2xl object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return <AgentAvatar name={agent.name} seed={agent.id} avatar={agent.avatar} size={size} round />;
}

const SOURCE_LABEL: Record<string, string> = {
  message: "Message",
  comms: "Comms",
  event: "Event",
  schedule: "Schedule",
  delegation: "Delegation",
  test: "Manual run",
};

interface Draft {
  name: string;
  role: string;
  description: string;
  avatar: string;
  // The charter (`instruction`) is shown as two files (soul.md + purpose.md); composed back into
  // the single field with section markers via shared splitCharter/joinCharter — no schema change.
  soul: string;
  purpose: string;
  autonomy: AgentAutonomy;
  // Connected Composio toolkit slugs the agent may use ([] = none, we keep the array).
  allowedApps: readonly string[];
  caps: Record<string, boolean>;
  // Round-tripped only — the agent manages its OWN wake cron; the user never edits it here.
  scheduleCron: string;
  keepWorking: boolean;
  dailyBudgetMinutes: number;
}

function draftFrom(a: Agent): Draft {
  const rawCaps = (a.capabilities ?? {}) as Record<string, unknown>;
  const caps: Record<string, boolean> = {};
  for (const [key] of CAP_KEYS) caps[key] = rawCaps[key] !== false;
  const { soul, purpose } = splitCharter(a.instruction);
  const das = a.budget?.dailyActiveSeconds ?? 0;
  return {
    name: a.name,
    role: a.role,
    description: a.description,
    avatar: a.avatar ?? "",
    soul,
    purpose,
    autonomy: a.autonomy,
    allowedApps: a.allowedApps ?? [],
    caps,
    scheduleCron: a.scheduleCron ?? "",
    keepWorking: a.keepWorking === 1,
    dailyBudgetMinutes: das > 0 ? Math.round(das / 60) : 0,
  };
}

// File upload for the agent composer. The turn accepts inline UploadChatAttachment
// ({type,name,mimeType,sizeBytes,dataUrl}); images inline natively, docs are text-extracted
// server-side. Narrow allowlist so a broadened accept can't smuggle arbitrary binaries.
const ATTACH_ACCEPT = "image/*,application/pdf,text/plain,text/markdown,text/csv,.md,.csv";
const ATTACH_DOC_MIME = /^(application\/pdf|text\/(plain|markdown|csv|tab-separated-values))/i;
const ATTACH_MAX_BYTES = 20 * 1024 * 1024;
function attachAllowed(file: File): boolean {
  return (
    file.size <= ATTACH_MAX_BYTES &&
    (file.type.startsWith("image/") || ATTACH_DOC_MIME.test(file.type))
  );
}
function attachKind(mimeType: string): "image" | "document" {
  return mimeType.startsWith("image/") ? "image" : "document";
}
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error("file read failed"));
    reader.readAsDataURL(file);
  });
}

// Human-friendly file size for the composer attachment cards (e.g. "1.4 MB").
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

// Browser Run sessions egress from whatever region Cloudflare places them (often
// abroad — e.g. Korea/Japan), so Google & co. localize the UI by egress IP regardless
// of Accept-Language. Force English on Google properties by pinning hl=en&gl=US, which
// Google honors over geo-IP. Left untouched for non-Google hosts.
function forceEnglish(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    if (/(^|\.)google\./i.test(u.hostname)) {
      if (!u.searchParams.has("hl")) u.searchParams.set("hl", "en");
      if (!u.searchParams.has("gl")) u.searchParams.set("gl", "US");
      return u.toString();
    }
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

const WAKE_ICON: Record<AgentWake["kind"], typeof AlarmClockIcon> = {
  schedule: AlarmClockIcon,
  "self-wake": SunIcon,
  pursue: RocketIcon,
  trigger: ZapIcon,
  board: MessagesSquareIcon,
  manual: PlayIcon,
  other: SunIcon,
};

/** Where the agent woke up on its own: when and why, then its work follows below it. */
function WakeDivider({ wake, at }: { wake: AgentWake; at: string }) {
  const Icon = WAKE_ICON[wake.kind];
  return (
    <div
      data-wake-at={Date.parse(at) || 0}
      className="mt-5 mb-1 flex scroll-mt-6 items-center gap-3 text-[11px] text-muted-foreground"
      role="note"
    >
      <span className="h-px flex-1 bg-border" aria-hidden />
      <span className="flex min-w-0 items-center gap-1.5">
        <Icon className="size-3.5 shrink-0" aria-hidden />
        <span className="font-medium text-foreground">Woke up</span>
        {wake.reason ? <span className="truncate">· {wake.reason}</span> : null}
        <span className="shrink-0 tabular-nums">· {timeOf(at)}</span>
      </span>
      <span className="h-px flex-1 bg-border" aria-hidden />
    </div>
  );
}

function timeOf(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function firstUrl(text: string): string {
  return text.match(/https?:\/\/[^\s)]+/)?.[0] ?? "";
}

function prettyUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.host + (u.pathname === "/" ? "" : u.pathname);
  } catch {
    return url;
  }
}

// The action-chip vocabulary — turns a raw step activity ("cloud_browser started",
// "COMPOSIO_SEARCH_WEB", "Browsing https://…") into a Manus-style typed chip: a mono icon
// + a friendly verb + target. The native engine emits either a tool id or free text, so we
// humanize known tool ids and sniff the leading verb otherwise.
type ActionKind =
  | "browse"
  | "scroll"
  | "click"
  | "run"
  | "file"
  | "search"
  | "approval"
  | "error"
  | "tool";

// Known tool ids → the words a person would use. Keys are lowercased tool names.
const TOOL_LABELS: Record<string, { verb: string; kind: ActionKind }> = {
  cloud_browser: { verb: "Cloud browser", kind: "browse" },
  browser: { verb: "Browser", kind: "browse" },
  browser_use: { verb: "Browser", kind: "browse" },
  composio_search_web: { verb: "Searching the web", kind: "search" },
  web_search: { verb: "Searching the web", kind: "search" },
  search: { verb: "Searching", kind: "search" },
  post_message: { verb: "Posted to board", kind: "tool" },
  read_board: { verb: "Checked the board", kind: "tool" },
  send_email: { verb: "Sending email", kind: "tool" },
  run_command: { verb: "Running", kind: "run" },
  playground: { verb: "Computer", kind: "run" },
  shell: { verb: "Running", kind: "run" },
  vault: { verb: "Files", kind: "file" },
  recall: { verb: "Recalling memory", kind: "tool" },
  remember: { verb: "Saving to memory", kind: "tool" },
};

function iconFor(kind: ActionKind): typeof GlobeIcon {
  switch (kind) {
    case "browse":
      return GlobeIcon;
    case "scroll":
      return ArrowDownIcon;
    case "click":
      return MousePointerClickIcon;
    case "run":
      return TerminalIcon;
    case "file":
      return FileTextIcon;
    case "search":
      return SearchIcon;
    case "approval":
      return ShieldCheckIcon;
    case "error":
      return TriangleAlertIcon;
    default:
      return WrenchIcon;
  }
}

function humanizeTool(id: string): string {
  const t = id.replace(/_/g, " ").trim().toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// Strip the status-verb noise the engine appends: "cloud_browser started" → "cloud_browser".
function coreOf(summary: string): string {
  return summary
    .trim()
    .replace(/\s+(started|finished|completed|succeeded|failed|running|done|call(?:ed)?)\b.*$/i, "")
    .trim();
}

// Dedupe key so a burst of identical steps collapses into one chip (with a ×N count).
function stepKey(summary: string, tone: string): string {
  return `${tone}:${coreOf(summary).toLowerCase().slice(0, 48)}`;
}

function classifyAction(
  summary: string,
  tone: string,
): { kind: ActionKind; Icon: typeof GlobeIcon; verb: string; detail: string; mono: boolean } {
  const s = summary.trim();
  const core = coreOf(s);
  const low = core.toLowerCase();
  const url = firstUrl(s);
  const strip = (re: RegExp) =>
    core
      .replace(re, "")
      .replace(/^[:\s–-]+/, "")
      .trim();
  const mk = (kind: ActionKind, verb: string, detail: string, mono: boolean) => ({
    kind,
    Icon: iconFor(kind),
    verb,
    detail,
    mono,
  });

  if (tone === "error") return mk("error", "Error", s, false);
  if (tone === "approval")
    return mk("approval", "Approval", strip(/^(waiting|awaiting|requesting)[^:]*:?/i), false);

  // A bare tool id (snake_case / CONSTANT_CASE) → its friendly label.
  const toolKey = /^[a-z][a-z0-9_]*$/i.test(core) ? low : "";
  const tool = toolKey ? TOOL_LABELS[toolKey] : undefined;
  if (tool)
    return mk(tool.kind, tool.verb, tool.kind === "browse" && url ? prettyUrl(url) : "", false);

  if (/scroll/.test(low)) return mk("scroll", "Scrolling", "", false);
  if (/\b(click|tap|press)\b/.test(low))
    return mk("click", "Clicking", strip(/^clicking( element)?/i), false);
  if (url || /\b(brows|navigat|visit|open(ing)? (the )?(url|page|site)|going to)\b/.test(low))
    return mk(
      "browse",
      "Browsing",
      url ? prettyUrl(url) : strip(/^(browsing|navigating to|visiting|opening)/i),
      false,
    );
  if (/\b(execut|shell|command)\b|running command|\brun(ning)?\b|^\$/.test(low))
    return mk(
      "run",
      "Running",
      strip(/^(executing( command)?|running( command)?|run|shell)/i),
      true,
    );
  if (/creat(e|ing) file|writing|wrote|saved|editing|read(ing)? file/.test(low)) {
    const path =
      s.match(/[/~][\w./-]+/)?.[0] ??
      strip(/^(creating file|writing|wrote|saved|editing|reading file|read file)/i);
    return mk("file", /read/.test(low) ? "Reading" : "Writing", path, true);
  }
  if (/search/.test(low)) return mk("search", "Searching", strip(/^searching( for)?/i), false);
  // Unknown snake_case tool id → humanize ("do_thing" → "Do thing"); else show as-is.
  return mk("tool", toolKey ? humanizeTool(core) : core, "", false);
}

/** A typed action chip — what the agent is doing, at a glance. Quiet on purpose: steps sit
 * between messages without competing with them. */
function ActionChip({
  summary,
  tone,
  count = 1,
}: {
  summary: string;
  tone: string;
  count?: number;
}) {
  const { Icon, verb, detail, mono, kind } = classifyAction(summary, tone);
  const iconClass =
    kind === "error" ? "text-destructive" : kind === "approval" ? "text-warning" : "";
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-muted/70 px-2.5 py-1 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground">
      <Icon className={`size-3.5 shrink-0 ${iconClass}`} />
      <span className="shrink-0 font-medium">{verb}</span>
      {detail ? (
        <span className={`min-w-0 truncate opacity-80 ${mono ? "font-mono text-[11px]" : ""}`}>
          {detail}
        </span>
      ) : null}
      {count > 1 ? <span className="shrink-0 tabular-nums opacity-70">×{count}</span> : null}
    </span>
  );
}

type Panel = "none" | "profile";

export function MorrowAgentWindowPage() {
  const navigate = useNavigate();
  const { agentId } = useParams({ strict: false }) as { agentId: string };

  // Seed from the last-good cache so opening an agent (esp. right after create, or
  // navigating back) renders instantly instead of flashing a loader / "not found".
  const [agent, setAgent] = useState<Agent | null>(
    () => cachedAgents()?.find((a) => a.id === agentId) ?? null,
  );
  const [runs, setRuns] = useState<readonly AgentRun[]>(() =>
    (cachedAgentRuns() ?? []).filter((r) => r.agentId === agentId),
  );
  const [draft, setDraft] = useState<Draft | null>(() => {
    const a = cachedAgents()?.find((x) => x.id === agentId);
    return a ? draftFrom(a) : null;
  });
  // Whether a load for THIS agentId has finished — so "not found" only shows once
  // we've actually confirmed absence (not during the initial/post-create fetch).
  const [settled, setSettled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [input, setInput] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [pending, setPending] = useState<readonly File[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  // Whether a drag is hovering the composer — drives the drop-zone highlight.
  const [dragOver, setDragOver] = useState(false);
  // Object URLs for image attachments so we can show real thumbnails in the composer
  // (not just a filename). Rebuilt whenever `pending` changes; the previous batch's URLs
  // are revoked in the cleanup below so we don't leak blobs.
  const previews = useMemo(
    () =>
      pending.map((f) => ({
        file: f,
        url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      })),
    [pending],
  );
  useEffect(
    () => () => {
      for (const p of previews) if (p.url) URL.revokeObjectURL(p.url);
    },
    [previews],
  );
  const [panel, setPanel] = useState<Panel>("none");
  // The Computer is a PERSISTENT right pane (Manus-style), not a modal — you watch it work
  // while the conversation keeps going. Screen = live browser, Terminal = shell.
  const [computerOpen, setComputerOpen] = useState(false);
  // Who is driving the computer. Taking over pauses the agent (its running turn is stopped)
  // and makes the live browser clickable; giving back resumes the agent if it was mid-task.
  const [userHasControl, setUserHasControl] = useState(false);
  // The run history / run log side panel (one right pane at a time: computer OR runs).
  const [runsOpen, setRunsOpen] = useState(false);
  const pausedForTakeover = useRef(false);

  // The agent's home Thread → a scoped ref (server-created threads sync in automatically).
  const threadRefs = useThreadRefs();
  const homeThreadId = agent?.homeThreadId ?? null;
  const homeRef = homeThreadId
    ? (threadRefs.find((r) => r.threadId === homeThreadId) ?? null)
    : null;

  const messages = useThreadMessages(homeRef);
  const session = useThreadSession(homeRef);
  const activities = useThreadActivities(homeRef);
  const isRunning = session?.status === "running" || session?.status === "starting";
  // Which step chip is expanded — click one to see exactly what that tool did (input + result).
  const [stepDetail, setStepDetail] = useState<(typeof activities)[number] | null>(null);

  // When a right pane (Computer or the step-detail inspector) is open we need the width, so we
  // fold the app sidebar away — and restore it (to whatever it was) when the pane closes. Keyed
  // only on rightOpen so if you manually reopen the rail while a pane is up, we don't fight you.
  const rightOpen = computerOpen || runsOpen || stepDetail !== null;
  const { open: sidebarOpen, setOpen: setSidebarOpen, isMobile } = useSidebar();
  const priorSidebarOpen = useRef<boolean | null>(null);
  useEffect(() => {
    if (rightOpen) {
      if (priorSidebarOpen.current === null) priorSidebarOpen.current = sidebarOpen;
      if (sidebarOpen) setSidebarOpen(false);
    } else if (priorSidebarOpen.current !== null) {
      if (priorSidebarOpen.current) setSidebarOpen(true);
      priorSidebarOpen.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightOpen]);

  // Live step visibility: interleave the agent's tool/step activities with its chat
  // bubbles, time-ordered, so you can watch WHAT it's doing (reads a file, runs a
  // shell command, browses) — not just the final reply. The native engine already
  // emits these as thread activities; the window just never showed them before.
  const timeline = useMemo(() => {
    type Row =
      | {
          readonly kind: "message";
          readonly t: number;
          readonly key: string;
          readonly m: (typeof messages)[number];
        }
      | {
          kind: "step";
          t: number;
          key: string;
          a: (typeof activities)[number];
          count: number;
        };
    const rows: Row[] = [];
    for (const m of messages) {
      if (m.role === "system") continue;
      rows.push({ kind: "message", t: Date.parse(m.createdAt) || 0, key: `m:${m.id}`, m });
    }
    for (const a of activities) {
      // Only STEP-like activities (tool runs, approvals, errors) — message activities
      // would duplicate the bubbles above.
      if (a.tone !== "tool" && a.tone !== "approval" && a.tone !== "error") continue;
      rows.push({ kind: "step", t: Date.parse(a.createdAt) || 0, key: `a:${a.id}`, a, count: 1 });
    }
    rows.sort((x, y) => x.t - y.t || (x.kind === "step" ? 1 : -1));
    // Collapse a burst of identical consecutive steps into ONE chip (cloud_browser ×6).
    const merged: Row[] = [];
    for (const r of rows) {
      const prev = merged[merged.length - 1];
      if (
        r.kind === "step" &&
        prev &&
        prev.kind === "step" &&
        stepKey(prev.a.summary, prev.a.tone) === stepKey(r.a.summary, r.a.tone)
      ) {
        prev.count += 1;
        prev.a = r.a; // keep the latest instance (freshest time/summary)
        prev.t = r.t;
        continue;
      }
      merged.push(r);
    }
    return merged;
  }, [messages, activities]);

  // What the chat renders: messages, with each run of consecutive steps folded into ONE row of
  // chips (so a burst of tool calls reads as a single line of activity, not a wall).
  const chatRows = useMemo(() => {
    type Step = { key: string; a: (typeof activities)[number]; count: number };
    type ChatRow =
      | {
          kind: "message";
          side: "me" | "agent";
          key: string;
          t: number;
          m: (typeof messages)[number];
        }
      | { kind: "steps"; side: "agent"; key: string; t: number; steps: Step[] }
      | { kind: "wake"; side: "wake"; key: string; t: number; wake: AgentWake; at: string };
    const out: ChatRow[] = [];
    for (const r of timeline) {
      if (r.kind === "step") {
        const last = out[out.length - 1];
        const step = { key: r.key, a: r.a, count: r.count };
        if (last?.kind === "steps") last.steps.push(step);
        else out.push({ kind: "steps", side: "agent", key: r.key, t: r.t, steps: [step] });
        continue;
      }
      const wake = r.m.role === "user" ? parseAgentWake(r.m.text) : null;
      if (wake) {
        out.push({ kind: "wake", side: "wake", key: r.key, t: r.t, wake, at: r.m.createdAt });
        continue;
      }
      out.push({
        kind: "message",
        side: r.m.role === "user" ? "me" : "agent",
        key: r.key,
        t: r.t,
        m: r.m,
      });
    }
    return out;
  }, [timeline]);

  // Your latest message counts as read once the agent has started on it (a reply or a step
  // after it, or a live turn) — shown as the mascot peeking over it, plus "Read <time>".
  const { seenMessageId, seenAt } = useMemo(() => {
    for (let i = chatRows.length - 1; i >= 0; i--) {
      const row = chatRows[i]!;
      if (row.kind !== "message" || row.side !== "me") continue;
      const next = chatRows[i + 1];
      if (next) return { seenMessageId: row.m.id, seenAt: timeOf(new Date(next.t).toISOString()) };
      return { seenMessageId: isRunning ? row.m.id : null, seenAt: null };
    }
    return { seenMessageId: null, seenAt: null };
  }, [chatRows, isRunning]);

  // Your last message, if it has sat unanswered (no reply, no step, agent idle) for 20s+.
  const [clock, setClock] = useState(() => Date.now());
  const lastRow = chatRows.at(-1);
  const waitingOn =
    lastRow?.kind === "message" && lastRow.side === "me" && !isRunning ? lastRow : null;
  useEffect(() => {
    if (!waitingOn) return;
    const t = setInterval(() => setClock(Date.now()), 5000);
    return () => clearInterval(t);
  }, [waitingOn]);
  const stalledMessageId = waitingOn && clock - waitingOn.t > 20_000 ? waitingOn.m.id : null;

  const palette = useMemo(
    () => agentPalette(agent?.avatar, agent?.id ?? agentId),
    [agent?.avatar, agent?.id, agentId],
  );

  // The URL the agent most recently browsed — drives the Computer's faux address bar &
  // "using Browser · <host>" status line, so the pane reads like a real browser window.
  const latestBrowseUrl = useMemo(() => {
    for (let i = activities.length - 1; i >= 0; i--) {
      const u = firstUrl(activities[i]?.summary ?? "");
      if (u) return u;
    }
    return null;
  }, [activities]);

  const startTurn = useAtomCommand(threadEnvironment.startTurn, { reportFailure: false });
  // Stop a running agent turn — reuses the SAME interrupt path as chat (thread.turn.interrupt →
  // aborts the morrow-agent AbortController for this thread). Works for the home thread here; the
  // headless-run case is stopped from the runs list by its own thread id.
  const interruptTurn = useAtomCommand(threadEnvironment.interruptTurn, { reportFailure: false });
  const [stopping, setStopping] = useState(false);
  const stopHomeTurn = useCallback(() => {
    if (!homeRef) return;
    setStopping(true);
    void interruptTurn({
      environmentId: homeRef.environmentId,
      input: { threadId: homeRef.threadId },
    });
  }, [homeRef, interruptTurn]);
  // Clear the local "Stopping…" state once the turn actually settles.
  useEffect(() => {
    if (!isRunning) setStopping(false);
  }, [isRunning]);

  // Voice dictation in the agent composer — reuses the shared hook (Web Speech with a
  // server-Whisper fallback) the general chat uses. Appends the transcript to the draft.
  const dictation = useComposerDictation({
    disabled: !homeRef || isRunning,
    onAppendText: (text) => setInput((prev) => joinPromptAndTranscript(prev, text)),
    onError: () => {},
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isRunning]);

  // A run from the history that happened right here in the chat: scroll to its "woke up" line
  // (the first one at or after the run was logged — a wake can wait for a busy turn to finish).
  const showWakeInChat = useCallback((run: AgentRun) => {
    const el = scrollRef.current;
    if (!el) return;
    const from = (Date.parse(run.createdAt) || 0) - 5000;
    const marks = Array.from(el.querySelectorAll<HTMLElement>("[data-wake-at]"));
    const mark = marks.find((m) => Number(m.dataset.wakeAt) >= from) ?? marks.at(-1);
    mark?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Auto-grow the composer with its content (ChatGPT/Claude style): collapse to measure,
  // then grow up to a cap; past the cap it scrolls. Re-runs on every keystroke via `input`.
  useEffect(() => {
    const el = composerRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [input]);

  const load = useCallback(async () => {
    // Silent revalidate (no setLoading(true)) — never flash over cached content.
    const [rows, runRows] = await Promise.all([fetchAgents(), fetchAgentRuns()]);
    const found = rows.find((r) => r.id === agentId) ?? null;
    setAgent(found);
    if (found) setDraft(draftFrom(found));
    setRuns(runRows.filter((r) => r.agentId === agentId));
    setSettled(true);
  }, [agentId]);

  useEffect(() => {
    setSettled(false);
    void load();
  }, [load]);

  // The chat pane below is already live (it subscribes to the home thread), but everything
  // around it — the header's status and next-wake, and the run list — was loaded once on mount
  // and then only refreshed by the Runs pane's own 15s timer, i.e. not at all while that pane is
  // closed. So an agent could wake, work and finish in front of the owner with the header still
  // claiming it was idle. Refetch on the server's `agents` poke instead.
  useMorrowSignal("agents", () => {
    invalidateAgents();
    void load();
  });

  const addFiles = useCallback((list: FileList | null) => {
    if (!list) return;
    const ok = Array.from(list).filter(attachAllowed);
    if (ok.length > 0) setPending((prev) => [...prev, ...ok]);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if ((!text && pending.length === 0) || !homeRef || isRunning) return;
    setInput("");
    const files = pending;
    setPending([]);
    // Inline upload attachments (dataUrl) — same shape ChatView sends. Images inline
    // natively; docs (pdf/text) are text-extracted server-side.
    const attachments = await Promise.all(
      files.map(async (f) => {
        const dataUrl = await readFileAsDataUrl(f);
        const base = { name: f.name, mimeType: f.type, sizeBytes: f.size, dataUrl };
        return attachKind(f.type) === "image"
          ? { type: "image" as const, ...base }
          : { type: "document" as const, ...base };
      }),
    );
    // Send the morrow-agent modelSelection every turn (same as ChatView/comms). sendTurn
    // only takes the native-engine branch when input.modelSelection.model is a
    // `morrow-agent:` model; omitting it made agentModel undefined → the turn fell through
    // to the adapter path (resolveRoutableSession → "Provider 'morrow-agent' is not
    // implemented"). The reactor still enforces the agent's persona/tools/autonomy.
    setSendError(null);
    const result = await startTurn({
      environmentId: homeRef.environmentId,
      input: {
        threadId: homeRef.threadId,
        message: { messageId: newMessageId(), role: "user", text, attachments },
        modelSelection: MORROW_AGENT_MODEL_SELECTION,
        runtimeMode: "full-access",
        interactionMode: "default",
        createdAt: new Date().toISOString(),
      },
    });
    // Never fail silently: put the text back and say so.
    if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
      setInput(text);
      setPending(files);
      setSendError("Couldn't send that. Check your connection and try again.");
    }
  }, [input, pending, homeRef, isRunning, startTurn]);

  // A message can be saved but never picked up (e.g. the server restarted for a deploy right as
  // it arrived). Re-run it cleanly: drop it and everything after, then start it again.
  const truncateMessages = useAtomCommand(threadEnvironment.truncateMessages, {
    reportFailure: false,
  });
  const retryMessage = useCallback(
    async (m: (typeof messages)[number]) => {
      if (!homeRef || isRunning) return;
      setSendError(null);
      const cut = await truncateMessages({
        environmentId: homeRef.environmentId,
        input: { threadId: homeRef.threadId, fromMessageId: m.id },
      });
      if (cut._tag === "Failure" && !isAtomCommandInterrupted(cut)) {
        setSendError("Couldn't retry that message. Try sending it again.");
        return;
      }
      const result = await startTurn({
        environmentId: homeRef.environmentId,
        input: {
          threadId: homeRef.threadId,
          message: { messageId: newMessageId(), role: "user", text: m.text, attachments: [] },
          modelSelection: MORROW_AGENT_MODEL_SELECTION,
          runtimeMode: "full-access",
          interactionMode: "default",
          createdAt: new Date().toISOString(),
        },
      });
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        setSendError("Couldn't retry that message. Try sending it again.");
      }
    },
    [homeRef, isRunning, truncateMessages, startTurn],
  );

  const canSend = (input.trim().length > 0 || pending.length > 0) && !!homeRef;

  // ---- Voice mode: talk to the agent; its replies are spoken and land in the chat as usual.
  const voiceSentAt = useRef(0);
  const sendVoiceText = useCallback(
    (text: string, task?: string) => {
      if (!homeRef) return;
      voiceSentAt.current = Date.now();
      void startTurn({
        environmentId: homeRef.environmentId,
        input: {
          threadId: homeRef.threadId,
          message: {
            messageId: newMessageId(),
            role: "user",
            text: voiceTurnText(text, task),
            attachments: [],
          },
          modelSelection: MORROW_AGENT_MODEL_SELECTION,
          runtimeMode: "full-access",
          interactionMode: "default",
          createdAt: new Date().toISOString(),
        },
      });
    },
    [homeRef, startTurn],
  );
  // The reply to speak: everything the agent has written since your last spoken turn.
  const voiceReply = useMemo<VoiceReply | null>(
    () => voiceReplyFrom(messages, voiceSentAt.current, isRunning),
    [messages, isRunning],
  );
  // What the fast voice model said (and what you said to it) goes into the chat as messages.
  const appendVoice = useAtomCommand(threadEnvironment.appendVoice, { reportFailure: false });
  const recordVoice = useCallback(
    (said: ReadonlyArray<{ readonly role: "user" | "assistant"; readonly text: string }>) => {
      if (!homeRef) return;
      void appendVoice({
        environmentId: homeRef.environmentId,
        input: {
          threadId: homeRef.threadId,
          messages: said.map((m) => ({
            messageId: newVoiceMessageId(),
            role: m.role,
            text: m.text,
          })),
        },
      });
    },
    [homeRef, appendVoice],
  );
  // The fast voice brain speaks AS this agent: its name, role and charter, plus the recent chat.
  const voiceBrain = useMemo(
    () =>
      agent
        ? {
            persona:
              `You are ${agentShortName(agent.name)}${agent.role ? `, ${agent.role}` : ""} — an AI ` +
              "teammate working for the person you're talking to, inside Morrow.\n\n" +
              (agent.instruction ? `Your charter:\n${agent.instruction.slice(0, 3000)}` : ""),
            history: () =>
              messages
                .filter((m) => m.role === "user" || m.role === "assistant")
                .slice(-10)
                .map((m) => {
                  const wake = m.role === "user" ? parseAgentWake(m.text) : null;
                  return {
                    role: m.role as "user" | "assistant",
                    text: wake
                      ? `(You woke up on your own: ${wake.reason})`
                      : visibleMessageText(m.text).slice(0, 1200),
                  };
                }),
          }
        : undefined,
    [agent, messages],
  );
  // Each agent starts with its own recognisable voice; you can pick another in voice mode.
  const [voiceId, setVoiceId] = useVoiceChoice(
    `agent:${agent?.id ?? "agent"}`,
    VOICE_OPTIONS[hueFrom(agent?.id ?? "agent") % VOICE_OPTIONS.length]?.id ?? "luna",
  );
  const voice = useVoiceConversation({
    reply: voiceReply,
    busy: isRunning,
    onUtterance: sendVoiceText,
    onExchange: recordVoice,
    onDelegate: (said, task, ack) => {
      sendVoiceText(said, task);
      if (ack) recordVoice([{ role: "assistant", text: ack }]);
    },
    ...(voiceBrain ? { brain: voiceBrain } : {}),
    onInterrupt: stopHomeTurn,
    ...(agent ? { name: agentShortName(agent.name) } : {}),
    speaker: voiceId,
  });

  // Voice mode makes room at the bottom of the chat; keep the newest message in view.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [voice.active]);

  const takeOver = useCallback(() => {
    pausedForTakeover.current = isRunning;
    if (isRunning) stopHomeTurn();
    setUserHasControl(true);
  }, [isRunning, stopHomeTurn]);

  const giveBack = useCallback(() => {
    setUserHasControl(false);
    if (!pausedForTakeover.current || !homeRef) return;
    pausedForTakeover.current = false;
    // Resume the task the agent was on when you took over.
    void startTurn({
      environmentId: homeRef.environmentId,
      input: {
        threadId: homeRef.threadId,
        message: {
          messageId: newMessageId(),
          role: "user",
          text: "I've finished using your browser. Carry on where you left off.",
          attachments: [],
        },
        modelSelection: MORROW_AGENT_MODEL_SELECTION,
        runtimeMode: "full-access",
        interactionMode: "default",
        createdAt: new Date().toISOString(),
      },
    }).catch(() => undefined);
  }, [homeRef, startTurn]);

  const save = useCallback(async () => {
    if (!agent || !draft || saving) return;
    setSaving(true);
    const ok = await updateAgent(agent.id, {
      name: draft.name.trim() || "Untitled agent",
      role: draft.role.trim(),
      description: draft.description.trim(),
      avatar: draft.avatar.trim() || null,
      instruction: joinCharter(draft.soul, draft.purpose),
      autonomy: draft.autonomy,
      // Strict opt-in: empty = NO apps (send []), consistent with create. `[]` = "none"
      // to the backend gate; null would mean "all connected apps".
      allowedApps: [...draft.allowedApps],
      capabilities: draft.caps,
      budget:
        draft.dailyBudgetMinutes > 0
          ? { ...(agent.budget ?? {}), dailyActiveSeconds: draft.dailyBudgetMinutes * 60 }
          : { ...(agent.budget ?? {}), dailyActiveSeconds: 0 },
      // Preserve the agent's own wake cron unchanged — the user doesn't manage it here.
      scheduleCron: draft.scheduleCron.trim() || null,
    });
    // "Keep working" is a separate opt-in write (arms/disarms the loop) — only when it changed.
    if (ok && draft.keepWorking !== (agent.keepWorking === 1)) {
      await setAgentKeepWorking(agent.id, draft.keepWorking);
    }
    setSaving(false);
    if (ok) await load();
  }, [agent, draft, saving, load]);

  const toggleStatus = useCallback(async () => {
    if (!agent || busy) return;
    setBusy(true);
    await setAgentStatus(agent.id, agent.status === "paused" ? "active" : "paused");
    setBusy(false);
    await load();
  }, [agent, busy, load]);

  const doRun = useCallback(async () => {
    if (!agent || busy) return;
    setBusy(true);
    await runAgent(agent.id);
    setBusy(false);
    await load();
  }, [agent, busy, load]);

  const remove = useCallback(async () => {
    if (!agent || busy) return;
    if (!globalThis.confirm(`Delete agent "${agent.name}"? This can't be undone.`)) return;
    setBusy(true);
    const ok = await deleteAgent(agent.id);
    setBusy(false);
    if (ok) void navigate({ to: "/agents" });
  }, [agent, busy, navigate]);

  const addTag = useCallback(async () => {
    if (!agent || !newTag.trim()) return;
    const ok = await upsertAgentTag(agent.id, newTag.trim());
    if (ok) {
      setNewTag("");
      await load();
    }
  }, [agent, newTag, load]);

  const removeTag = useCallback(
    async (handle: string, channelScope: string) => {
      if (!agent) return;
      if (await deleteAgentTag(handle, channelScope)) await load();
    },
    [agent, load],
  );

  const statusLabel = useMemo(() => {
    if (isRunning) return "Thinking…";
    if (!agent) return "";
    if (agent.status === "paused") return "Paused";
    return agent.role || "Ready";
  }, [isRunning, agent]);

  // Centered spinner in the main pane until this agentId has settled; only render
  // "not found" once a load actually confirmed the agent is absent (no post-create flash).
  const spinner = (
    <SidebarInset className="flex h-dvh items-center justify-center bg-background text-muted-foreground">
      <Loader2Icon className="size-5 animate-spin" />
    </SidebarInset>
  );
  if (!agent) {
    if (!settled) return spinner;
    return (
      <SidebarInset className="flex h-dvh flex-col items-center justify-center gap-3 bg-background">
        <div className="text-sm font-medium text-foreground">Agent not found</div>
        <Button variant="outline" onClick={() => void navigate({ to: "/agents" })}>
          Back to agents
        </Button>
      </SidebarInset>
    );
  }
  if (!draft) return spinner;

  return (
    <SidebarInset className="relative h-dvh min-h-0 overflow-hidden bg-card text-foreground">
      <div className="flex h-full min-h-0">
        {/* Conversation column: the agent floats over the top of its chat, the composer floats
            at the bottom, and messages scroll (and fade) underneath both. */}
        <div className="relative flex min-w-0 flex-1 flex-col bg-card">
          {/* Solid behind the mascot, name and status, then a short fade — messages slide under
              it without ever showing through the text. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 z-10 h-40 bg-gradient-to-b from-card from-75% to-transparent"
          />
          {/* Three columns so the agent stays centred over its chat whatever sits either side. */}
          <header className="pointer-events-none absolute inset-x-0 top-0 z-20 grid grid-cols-[1fr_auto_1fr] items-start px-3 pt-3">
            {/* With the app sidebar collapsed its floating toggle sits in the top-left corner,
                so the back button steps right to stay clear of it. */}
            <div className={`flex justify-start ${sidebarOpen && !isMobile ? "" : "pl-11"}`}>
              <button
                type="button"
                onClick={() => void navigate({ to: "/agents" })}
                className={FLOAT_BUTTON}
                aria-label="Back to agents"
                title="Back to agents"
              >
                <ArrowLeftIcon className="size-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setPanel("profile")}
              className="pointer-events-auto group flex flex-col items-center rounded-2xl px-2 pb-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              title={`${agent.name}: profile and settings`}
            >
              <span className="agent-mascot-bob block" data-thinking={isRunning}>
                <AgentMascot agent={agent} size={56} thinking={isRunning} />
              </span>
              <span className="-mt-1.5 max-w-[220px] truncate rounded-full bg-card px-2.5 py-0.5 text-xs font-medium text-foreground shadow-sm ring-1 ring-border transition group-hover:ring-ring/40">
                {agentShortName(agent.name)}
              </span>
              <span
                className={`mt-0.5 text-[11px] ${isRunning ? "animate-pulse text-foreground/70" : "text-muted-foreground"}`}
              >
                {statusLabel}
              </span>
            </button>
            <div className="flex items-center justify-end gap-1.5">
              <button
                type="button"
                aria-disabled
                className={`${FLOAT_BUTTON} cursor-default`}
                aria-label="Call (coming soon)"
                title={`Calling ${agentShortName(agent.name)} is coming soon`}
              >
                <PhoneIcon className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setRunsOpen((v) => !v);
                  setComputerOpen(false);
                }}
                className={`${FLOAT_BUTTON} ${runsOpen ? "bg-accent text-foreground" : ""}`}
                title={runsOpen ? "Hide runs" : "Runs and logs"}
                aria-label="Runs"
                aria-pressed={runsOpen}
              >
                <HistoryIcon className="size-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setComputerOpen((v) => !v);
                  setRunsOpen(false);
                }}
                className={`${FLOAT_BUTTON} ${computerOpen ? "bg-accent text-foreground" : ""}`}
                title={
                  computerOpen
                    ? "Hide the computer"
                    : `Open ${agentShortName(agent.name)}'s computer`
                }
                aria-label="Computer"
                aria-pressed={computerOpen}
              >
                <MonitorIcon className="size-4" />
              </button>
            </div>
          </header>

          {/* Chat body */}
          <div
            ref={scrollRef}
            className={`relative flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pt-40 ${
              voice.active ? "pb-72" : "pb-32"
            }`}
          >
            <div className="mx-auto flex w-full max-w-[720px] flex-1 flex-col">
              {messages.length === 0 && !isRunning ? (
                <div className="m-auto flex max-w-sm flex-col items-center gap-1.5 text-center">
                  <div className="text-[15px] font-medium text-foreground">
                    Say hi to {agentShortName(agent.name)}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Ask it to do something. Open its computer to watch it work, or take over when
                    you need to.
                  </div>
                </div>
              ) : null}
              {chatRows.map((row, i) => {
                const prev = chatRows[i - 1];
                // Tight spacing inside one speaker's run, a clear break when the speaker changes.
                const gap = !prev ? "" : prev.side === row.side ? "mt-1" : "mt-4";
                if (row.kind === "wake") {
                  return <WakeDivider key={row.key} wake={row.wake} at={row.at} />;
                }
                if (row.kind === "steps") {
                  return (
                    <div key={row.key} className={`flex flex-wrap gap-1.5 ${gap}`}>
                      {row.steps.map((s) => (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setStepDetail(s.a)}
                          className="max-w-full rounded-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          title={`${timeOf(s.a.createdAt)} · see what this step did`}
                        >
                          <ActionChip summary={s.a.summary} tone={s.a.tone} count={s.count} />
                        </button>
                      ))}
                    </div>
                  );
                }
                const m = row.m;
                const mine = m.role === "user";
                // Show the typing caret ONLY on the last message while the turn is live.
                // The native agent engine can leave a stale `streaming: true` on finished
                // messages; guarding on isRunning + last-index stops the caret sticking on
                // every old reply (the "never finishes" look).
                const showCaret = m.streaming && isRunning && m.id === messages.at(-1)?.id;
                const seen = mine && m.id === seenMessageId;
                return (
                  <div
                    key={row.key}
                    className={`flex w-full flex-col ${mine ? "items-end" : "items-start"} ${gap}`}
                  >
                    <div
                      title={timeOf(m.createdAt)}
                      className={`relative rounded-[20px] px-4 py-2.5 text-sm leading-relaxed ${
                        mine ? "max-w-[75%]" : "max-w-[85%] bg-muted text-foreground"
                      }`}
                      style={mine ? { background: palette.fill, color: palette.ink } : undefined}
                    >
                      {seen ? (
                        // The agent peeking over your message: it has read it.
                        <span
                          className="pointer-events-none absolute -left-2.5 -top-3.5"
                          aria-hidden
                        >
                          <AgentMascot agent={agent} size={22} />
                        </span>
                      ) : null}
                      {mine ? (
                        <div className="whitespace-pre-wrap break-words">
                          {visibleMessageText(m.text)}
                          {showCaret ? <span className="ml-0.5 animate-pulse">▍</span> : null}
                        </div>
                      ) : (
                        // Render the agent's reply as real Markdown (headings, lists, links,
                        // code) instead of dumping raw ** and ### — the shared chat renderer.
                        <ChatMarkdown
                          text={m.text}
                          cwd={undefined}
                          isStreaming={showCaret}
                          className="text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_.chat-markdown-codeblock]:border-transparent [&_.chat-markdown-codeblock]:bg-card [&_a]:break-words [&_h1]:text-[15px] [&_h2]:text-[15px] [&_h3]:text-sm [&_h3]:font-semibold [&_hr]:border-border [&_pre]:max-w-full [&_pre]:overflow-x-auto"
                        />
                      )}
                    </div>
                    {m.id === stalledMessageId ? (
                      <span className="mt-1 flex items-center gap-1.5 pr-1 text-[11px] text-muted-foreground">
                        Didn&rsquo;t start
                        <button
                          type="button"
                          onClick={() => void retryMessage(m)}
                          className="rounded-full px-2 py-0.5 font-medium text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          Retry
                        </button>
                      </span>
                    ) : null}
                    {seen && seenAt ? (
                      <span className="mt-1 pr-1 text-[11px] text-muted-foreground">
                        Read {seenAt}
                      </span>
                    ) : null}
                  </div>
                );
              })}
              {isRunning && !messages.at(-1)?.streaming ? (
                <div className="mt-4 flex justify-start">
                  <div className="rounded-[20px] bg-muted px-4 py-3" aria-label="Thinking">
                    <span className="flex gap-1">
                      <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/70 [animation-delay:-0.2s]" />
                      <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/70 [animation-delay:-0.1s]" />
                      <span className="size-1.5 animate-bounce rounded-full bg-muted-foreground/70" />
                    </span>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Composer — a slim floating pill: + to attach, the message, mic, send. Attachment
              previews sit inside the pill above the row. The whole pill is a drop zone and
              accepts pasted images. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-card from-55% to-transparent px-4 pb-4 pt-10">
            <input
              ref={fileInputRef}
              type="file"
              accept={ATTACH_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {voice.active ? (
              <VoicePanel
                status={voice.status}
                caption={voice.caption}
                muted={voice.muted}
                error={voice.error}
                name={agentShortName(agent.name)}
                color={palette.fill}
                levels={voice.levels}
                voice={voiceId}
                onVoiceChange={setVoiceId}
                onToggleMute={() => voice.setMuted(!voice.muted)}
                onEnd={voice.stop}
              />
            ) : null}
            <div
              hidden={voice.active}
              onDragOver={(e) => {
                if (!homeRef) return;
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={(e) => {
                // Only clear when the pointer actually leaves the composer, not a child.
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (homeRef) addFiles(e.dataTransfer.files);
              }}
              className={`pointer-events-auto mx-auto flex w-full max-w-[720px] flex-col gap-2 rounded-[24px] border bg-card p-1.5 shadow-[0_2px_14px_rgb(0_0_0/0.06)] transition ${
                dragOver
                  ? "border-ring ring-2 ring-ring/30"
                  : "border-border focus-within:border-ring/50"
              }`}
            >
              {/* Attachment previews — image thumbnails, docs as file cards */}
              {previews.length > 0 ? (
                <div className="flex flex-wrap gap-2 px-1.5 pt-1.5">
                  {previews.map((p, i) => {
                    const remove = () => setPending((prev) => prev.filter((_, j) => j !== i));
                    return p.url ? (
                      <div
                        key={`${p.file.name}-${i}`}
                        className="group relative size-16 overflow-hidden rounded-xl border border-border bg-muted"
                      >
                        <img src={p.url} alt={p.file.name} className="size-full object-cover" />
                        <button
                          type="button"
                          aria-label={`Remove ${p.file.name}`}
                          onClick={remove}
                          className="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
                        >
                          <XIcon className="size-3" />
                        </button>
                      </div>
                    ) : (
                      <div
                        key={`${p.file.name}-${i}`}
                        className="flex max-w-[220px] items-center gap-2 rounded-xl border border-border bg-muted/50 py-1.5 pl-2 pr-1"
                      >
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-card text-muted-foreground">
                          <FileTextIcon className="size-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-xs font-medium text-foreground">
                            {p.file.name}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {formatBytes(p.file.size)}
                          </div>
                        </div>
                        <button
                          type="button"
                          aria-label={`Remove ${p.file.name}`}
                          onClick={remove}
                          className="ml-1 shrink-0 rounded-full p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground"
                        >
                          <XIcon className="size-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {sendError ? (
                <div className="px-3 pt-1 text-xs text-destructive" role="alert">
                  {sendError}
                </div>
              ) : null}
              {dictation.isListening ? (
                <div className="flex items-center gap-2 px-3 pt-1 text-xs text-muted-foreground">
                  <span className="size-1.5 animate-pulse rounded-full bg-destructive" />
                  <span>{dictation.interimText ? dictation.interimText : "Listening…"}</span>
                </div>
              ) : null}

              <div className="flex items-end gap-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!homeRef}
                  aria-label="Attach files"
                  title="Attach files"
                  className={COMPOSER_ICON_BUTTON}
                >
                  <PlusIcon className="size-[18px]" />
                </button>
                <textarea
                  ref={composerRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  onPaste={(e) => {
                    const files = Array.from(e.clipboardData.files);
                    if (files.length > 0) {
                      e.preventDefault();
                      addFiles(e.clipboardData.files);
                    }
                  }}
                  placeholder={homeRef ? "Send a message" : "Preparing conversation…"}
                  disabled={!homeRef}
                  className="max-h-[200px] min-h-[36px] flex-1 resize-none bg-transparent px-1 py-2 text-sm leading-5 text-foreground placeholder:text-muted-foreground focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => dictation.toggle()}
                  disabled={!homeRef || isRunning}
                  aria-label={dictation.isListening ? "Stop dictation" : "Dictate"}
                  title="Dictate with your microphone"
                  className={`${COMPOSER_ICON_BUTTON} ${dictation.isListening ? "bg-destructive/10 text-destructive" : ""}`}
                >
                  <MicIcon className="size-[18px]" />
                </button>
                {isRunning ? (
                  <button
                    type="button"
                    onClick={stopHomeTurn}
                    disabled={!homeRef || stopping}
                    className="flex size-9 shrink-0 items-center justify-center rounded-full bg-foreground text-background transition hover:opacity-90 disabled:opacity-40"
                    aria-label={stopping ? "Stopping…" : "Stop"}
                    title={stopping ? "Stopping…" : "Stop"}
                  >
                    {stopping ? (
                      <span className="size-3.5 animate-spin rounded-full border-2 border-background/40 border-t-background" />
                    ) : (
                      <span className="size-3 rounded-[3px] bg-current" aria-hidden />
                    )}
                  </button>
                ) : !canSend && homeRef ? (
                  <button
                    type="button"
                    onClick={() => void voice.start()}
                    className="flex size-9 shrink-0 items-center justify-center rounded-full transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Talk to ${agentShortName(agent.name)}`}
                    title={`Talk to ${agentShortName(agent.name)}`}
                  >
                    <VoiceOrb size={34} color={palette.fill} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void send()}
                    disabled={!canSend}
                    className="flex size-9 shrink-0 items-center justify-center rounded-full transition hover:brightness-95 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                    style={canSend ? { background: palette.fill, color: palette.ink } : undefined}
                    aria-label="Send"
                    title="Send"
                  >
                    <ArrowUpIcon className="size-[18px]" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* The agent's Computer — a persistent right pane you watch while it works. */}
        {computerOpen ? (
          <ComputerPane
            agent={agent}
            palette={palette}
            messages={messages}
            browseUrl={latestBrowseUrl}
            running={isRunning}
            userHasControl={userHasControl}
            onTakeOver={takeOver}
            onGiveBack={giveBack}
            onClose={() => {
              setComputerOpen(false);
              if (userHasControl) giveBack();
            }}
          />
        ) : null}
        {runsOpen ? (
          <RunsPane
            agent={agent}
            runs={runs}
            onRefresh={load}
            onClose={() => setRunsOpen(false)}
            onStep={setStepDetail}
            onShowInChat={showWakeInChat}
          />
        ) : null}
        {/* Step inspector — click any action chip to see exactly what that tool did, docked right. */}
        {stepDetail ? (
          <StepDetail activity={stepDetail} onClose={() => setStepDetail(null)} />
        ) : null}
      </div>

      {/* Profile drawer (tap the name — like a WhatsApp contact) */}
      {panel === "profile" ? (
        <ProfileDrawer
          agent={agent}
          draft={draft}
          setDraft={setDraft}
          runs={runs}
          saving={saving}
          busy={busy}
          newTag={newTag}
          setNewTag={setNewTag}
          onClose={() => setPanel("none")}
          onSave={save}
          onToggleStatus={toggleStatus}
          onRun={doRun}
          onDelete={remove}
          onAddTag={addTag}
          onRemoveTag={removeTag}
        />
      ) : null}
    </SidebarInset>
  );
}

/* ------------------------------------------------------------------ */

/** Centered modal — the agent surfaces open here. `fixed inset-0` so the dim covers the WHOLE
 * viewport uniformly (an `absolute` overlay only dims its column — the uneven-backdrop bug). */
function Drawer({
  children,
  onClose,
  size = "md",
}: {
  children: ReactNode;
  onClose: () => void;
  size?: "md" | "lg" | "xl";
}) {
  const maxW = size === "xl" ? "max-w-3xl" : size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl ${maxW}`}
      >
        {children}
      </div>
    </div>
  );
}

/** Format an activity payload for display: pretty JSON for objects, raw text for strings. */
function formatPayload(payload: unknown): string {
  if (payload == null) return "";
  if (typeof payload === "string") return payload;
  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

/** The step inspector — a right-docked panel showing exactly what one action chip did:
 * its verb, kind, time, the full summary, and the raw tool payload (input + result). */
function StepDetail({
  activity,
  onClose,
}: {
  activity: OrchestrationThreadActivity;
  onClose: () => void;
}) {
  const { Icon, verb, kind } = classifyAction(activity.summary, activity.tone);
  const iconClass =
    kind === "error"
      ? "text-red-500"
      : kind === "approval"
        ? "text-amber-500"
        : "text-muted-foreground";
  const payloadText = formatPayload(activity.payload);
  return (
    <div className="relative flex min-h-0 w-full flex-col border-l border-border bg-background max-md:absolute max-md:inset-0 max-md:z-40 md:w-[42%] md:min-w-[380px] md:max-w-[640px]">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-2.5 border-b border-border bg-card px-3 py-2.5">
        <Icon className={`size-4 shrink-0 ${iconClass}`} />
        <div className="min-w-0">
          <div className="truncate text-[13px] font-semibold text-foreground">{verb}</div>
          <div className="truncate text-[11px] text-muted-foreground">
            {activity.kind} · {timeOf(activity.createdAt)}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="ml-auto rounded-md p-1.5 text-muted-foreground transition hover:bg-accent/50 hover:text-foreground"
          aria-label="Close step detail"
          title="Close"
        >
          <ChevronRightIcon className="size-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
        <div>
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            What happened
          </div>
          <div className="whitespace-pre-wrap break-words rounded-lg border border-border bg-card/60 p-3 text-sm text-foreground">
            {activity.summary}
          </div>
        </div>
        {payloadText ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              Details
            </div>
            <pre className="min-h-0 flex-1 overflow-auto rounded-lg border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed text-foreground">
              {payloadText}
            </pre>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            No extra detail was recorded for this step.
          </div>
        )}
      </div>
    </div>
  );
}

/** What started a run, in words people use. */
function runLabel(run: AgentRun): string {
  if (run.source === "schedule") {
    if (run.detail === "self-wake") return "Woke itself";
    if (run.detail === "pursue") return "Pursuing a goal";
    if (run.detail?.startsWith("paused")) return "Paused itself";
    return "Scheduled";
  }
  if (run.source === "test") return "Run now";
  if (run.source === "trigger") return "Trigger";
  if (run.source === "comms") return "Message";
  if (run.source === "delegation") return "Board wake";
  return SOURCE_LABEL[run.source] ?? run.source;
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

type RunState = "running" | "done" | "failed" | "unlinked";

function RunStatePill({ state }: { state: RunState }) {
  const style: Record<RunState, string> = {
    running: "bg-primary/10 text-primary",
    done: "bg-muted text-muted-foreground",
    failed: "bg-destructive/10 text-destructive",
    unlinked: "bg-muted text-muted-foreground/70",
  };
  const label: Record<RunState, string> = {
    running: "Running",
    done: "Done",
    failed: "Failed",
    unlinked: "No log",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${style[state]}`}
    >
      {state === "running" ? (
        <span className="size-1.5 animate-pulse rounded-full bg-current" />
      ) : null}
      {label[state]}
    </span>
  );
}

/** One run row: label, detail, time and its live state (read from the run's own thread). */
function RunRow({
  run,
  inHomeChat,
  latest,
  onOpen,
}: {
  run: AgentRun;
  /** It ran in the agent's own chat (so the chat's live state is only this run's if it's the latest). */
  inHomeChat: boolean;
  latest: boolean;
  onOpen: () => void;
}) {
  const refs = useThreadRefs();
  const ref = run.threadId ? (refs.find((r) => r.threadId === run.threadId) ?? null) : null;
  const session = useThreadSession(ref);
  const state: RunState =
    run.status === "failed"
      ? "failed"
      : !run.threadId
        ? "unlinked"
        : (session?.status === "running" || session?.status === "starting") &&
            (!inHomeChat || latest)
          ? "running"
          : "done";
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!run.threadId}
      className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-accent disabled:cursor-default disabled:hover:bg-transparent"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{runLabel(run)}</div>
        <div className="truncate text-xs text-muted-foreground">
          {whenLabel(run.createdAt)}
          {run.detail && !["self-wake", "pursue", "manual run"].includes(run.detail)
            ? ` · ${run.detail}`
            : ""}
        </div>
      </div>
      <RunStatePill state={state} />
      {run.threadId ? <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" /> : null}
    </button>
  );
}

/** The full log of one run: the task it was given, every step it took, and what it said. */
function RunLog({
  run,
  onBack,
  onStep,
}: {
  run: AgentRun;
  onBack: () => void;
  onStep: (a: OrchestrationThreadActivity) => void;
}) {
  const refs = useThreadRefs();
  const ref = run.threadId ? (refs.find((r) => r.threadId === run.threadId) ?? null) : null;
  const messages = useThreadMessages(ref);
  const activities = useThreadActivities(ref);
  const session = useThreadSession(ref);
  const running = session?.status === "running" || session?.status === "starting";

  type Row =
    | { kind: "message"; t: number; key: string; m: (typeof messages)[number] }
    | {
        kind: "steps";
        t: number;
        key: string;
        steps: Array<{ a: (typeof activities)[number]; count: number }>;
      };
  const rows = useMemo(() => {
    const raw: Array<{
      t: number;
      key: string;
      m?: (typeof messages)[number];
      a?: (typeof activities)[number];
    }> = [];
    for (const m of messages) {
      if (m.role === "system") continue;
      raw.push({ t: Date.parse(m.createdAt) || 0, key: `m:${m.id}`, m });
    }
    for (const a of activities) {
      if (a.tone !== "tool" && a.tone !== "approval" && a.tone !== "error") continue;
      raw.push({ t: Date.parse(a.createdAt) || 0, key: `a:${a.id}`, a });
    }
    raw.sort((x, y) => x.t - y.t);
    const out: Row[] = [];
    for (const r of raw) {
      if (r.m) {
        out.push({ kind: "message", t: r.t, key: r.key, m: r.m });
        continue;
      }
      const a = r.a!;
      const last = out[out.length - 1];
      if (last?.kind === "steps") {
        const prev = last.steps[last.steps.length - 1]!;
        if (stepKey(prev.a.summary, prev.a.tone) === stepKey(a.summary, a.tone)) {
          prev.count += 1;
          prev.a = a;
        } else last.steps.push({ a, count: 1 });
      } else out.push({ kind: "steps", t: r.t, key: r.key, steps: [{ a, count: 1 }] });
    }
    return out;
  }, [messages, activities]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center gap-2 px-3 pb-2">
        <button
          type="button"
          onClick={onBack}
          className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
          aria-label="Back to all runs"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium text-foreground">{runLabel(run)}</div>
          <div className="truncate text-xs text-muted-foreground">{whenLabel(run.createdAt)}</div>
        </div>
        <RunStatePill state={run.status === "failed" ? "failed" : running ? "running" : "done"} />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6">
        {run.error ? (
          <div className="mb-3 rounded-xl bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {run.error}
          </div>
        ) : null}
        {!ref ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            This run&rsquo;s log isn&rsquo;t loaded yet.
          </div>
        ) : null}
        <div className="flex flex-col gap-2">
          {rows.map((row, i) => {
            if (row.kind === "steps") {
              return (
                <div key={row.key} className="flex flex-wrap gap-1.5">
                  {row.steps.map((s) => (
                    <button
                      key={s.a.id}
                      type="button"
                      onClick={() => onStep(s.a)}
                      className="max-w-full rounded-full text-left"
                      title={`${timeOf(s.a.createdAt)} · see what this step did`}
                    >
                      <ActionChip summary={s.a.summary} tone={s.a.tone} count={s.count} />
                    </button>
                  ))}
                </div>
              );
            }
            const m = row.m;
            if (m.role === "user") {
              // The run's instructions (persona + task) — collapsed; the first line is the task.
              return (
                <details
                  key={row.key}
                  className="group rounded-xl bg-muted/60 px-3 py-2 text-xs"
                  open={i === 0 && rows.length === 1}
                >
                  <summary className="cursor-pointer list-none font-medium text-muted-foreground">
                    Task given to the agent
                  </summary>
                  <div className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap break-words text-muted-foreground">
                    {m.text}
                  </div>
                </details>
              );
            }
            return (
              <div key={row.key} className="rounded-[20px] bg-muted px-4 py-2.5">
                <ChatMarkdown
                  text={m.text}
                  cwd={undefined}
                  isStreaming={running && m.id === messages.at(-1)?.id}
                  className="text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_.chat-markdown-codeblock]:border-transparent [&_.chat-markdown-codeblock]:bg-card [&_a]:break-words [&_pre]:max-w-full [&_pre]:overflow-x-auto"
                />
              </div>
            );
          })}
          {running ? (
            <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
              <Loader2Icon className="size-3.5 animate-spin" /> Working…
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/** Every run this agent has done on its own (and on request), newest first; open one to see
 * exactly what happened. Refreshes while open so new self-wakes appear as they start. */
function RunsPane({
  agent,
  runs,
  onRefresh,
  onClose,
  onStep,
  onShowInChat,
}: {
  agent: Agent;
  runs: readonly AgentRun[];
  onRefresh: () => void;
  onClose: () => void;
  onStep: (a: OrchestrationThreadActivity) => void;
  /** Show a run that happened in the agent's own chat, where it is in the chat. */
  onShowInChat: (run: AgentRun) => void;
}) {
  const [open, setOpen] = useState<AgentRun | null>(null);
  useEffect(() => {
    const t = setInterval(onRefresh, 15000);
    return () => clearInterval(t);
  }, [onRefresh]);
  const name = agentShortName(agent.name);
  return (
    <div className="relative flex min-h-0 w-full flex-col border-l border-border bg-background max-md:absolute max-md:inset-0 max-md:z-30 md:w-[40%] md:min-w-[380px] md:max-w-[560px]">
      <div className="flex h-12 shrink-0 items-center gap-2 px-3">
        <HistoryIcon className="size-4 text-muted-foreground" />
        <div className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
          {name}&rsquo;s runs
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
          aria-label="Hide runs"
          title="Hide runs"
        >
          <PanelRightIcon className="size-4" />
        </button>
      </div>
      {!open ? (
        <div className="mx-3 mb-2 flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          <ClockIcon className="size-3.5 shrink-0" />
          {agent.status !== "active" ? (
            <span>Paused. It won&rsquo;t wake on its own until you resume it.</span>
          ) : agent.nextRunAt ? (
            <span>
              Next wake{" "}
              <span className="font-medium text-foreground">{whenLabel(agent.nextRunAt)}</span>
              {agent.keepWorking === 1 ? " · keeps working on its own" : ""}
            </span>
          ) : agent.keepWorking === 1 ? (
            <span>Keeps working on its own; the next wake is set after its current work.</span>
          ) : (
            <span>Wakes only when you message it, a trigger fires or another agent asks.</span>
          )}
        </div>
      ) : null}
      {open ? (
        <RunLog run={open} onBack={() => setOpen(null)} onStep={onStep} />
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          {runs.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              No runs yet. When {name} wakes itself, follows a schedule or reacts to a trigger, each
              run shows up here with its full log.
            </div>
          ) : (
            runs.slice(0, 100).map((r, i) => {
              const inHomeChat = !!r.threadId && r.threadId === agent.homeThreadId;
              return (
                <RunRow
                  key={r.id}
                  run={r}
                  inHomeChat={inHomeChat}
                  latest={i === 0}
                  onOpen={() => (inHomeChat ? onShowInChat(r) : setOpen(r))}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// The live view renders a real 1280×720 Chrome viewport under its own tab/URL bar. We lay it out
// at that native size and scale the whole frame down uniformly, so the browser's bar and the page
// shrink together — otherwise the bar stays full-size while the page stream shrinks to fit.
// Measured: the live view's tab/URL bar is 70px over a 1280×764 page.
const LIVE_WIDTH = 1280;
const LIVE_HEIGHT = 834;
// Browser Use sizes the remote SCREEN, and its desktop + Chrome UI take ~256px of that height
// (a 720px screen leaves a 464px page). Ask for a taller screen so the page itself is ~1280×720.
const SCREEN_HEIGHT = 976;

/** Fills its box with a LIVE_WIDTH×LIVE_HEIGHT iframe scaled to fit (pointer input still maps). */
function ScaledLiveView({
  src,
  title,
  interactive,
}: {
  src: string;
  title: string;
  interactive: boolean;
}) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const fit = () =>
      setScale(Math.min(el.clientWidth / LIVE_WIDTH, el.clientHeight / LIVE_HEIGHT));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={boxRef} className="absolute inset-0 overflow-hidden bg-[var(--terminal-background)]">
      {scale > 0 ? (
        <iframe
          src={src}
          title={title}
          className={`absolute left-0 top-0 origin-top-left border-0 ${interactive ? "" : "pointer-events-none"}`}
          style={{ width: LIVE_WIDTH, height: LIVE_HEIGHT, transform: `scale(${scale})` }}
          allow="clipboard-read; clipboard-write"
        />
      ) : null}
    </div>
  );
}

type ComputerView = "browser" | "terminal";
interface ComputerTab {
  readonly id: number;
  readonly view: ComputerView;
}

/** The agent's live clock for its home screen — refreshed every 20s, enough for HH:MM. */
function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 20000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/**
 * The agent's COMPUTER — a right pane you watch while the conversation keeps going. A tab strip
 * on top ("Scout's computer", + for a second view), then a stage in the agent's colour holding
 * its screen: the live cloud browser, its terminal, or — when it isn't browsing — a home screen.
 * A dock switches Browser/Terminal, and a control pill under the stage says who is driving:
 * "Scout has control · Take over" pauses the agent and lets you use the browser yourself;
 * "Give back" hands it back and the agent picks up where it left off.
 * The browser bills while a session is open, so it starts only when the agent is browsing or
 * you ask for it, and it is stopped when the pane closes (unless a turn is still running).
 */
function ComputerPane({
  agent,
  palette,
  messages,
  browseUrl,
  running,
  userHasControl,
  onTakeOver,
  onGiveBack,
  onClose,
}: {
  agent: Agent;
  palette: AgentPalette;
  messages: ReadonlyArray<{ readonly id: string; readonly role: string; readonly text: string }>;
  browseUrl: string | null;
  running: boolean;
  userHasControl: boolean;
  onTakeOver: () => void;
  onGiveBack: () => void;
  onClose: () => void;
}) {
  const name = agentShortName(agent.name);
  const [tabs, setTabs] = useState<readonly ComputerTab[]>([{ id: 1, view: "browser" }]);
  const [activeId, setActiveId] = useState(1);
  const active = tabs.find((t) => t.id === activeId) ?? tabs[0]!;
  const setView = (view: ComputerView) =>
    setTabs((prev) => prev.map((t) => (t.id === active.id ? { ...t, view } : t)));

  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  // The Browser Use session id behind `liveUrl` — polled for liveness so a session that expires
  // (idle timeout, or the agent's ended) doesn't leave Browser Use's cross-origin "Connection
  // Lost" page stuck in our frame; instead we show our own restart state.
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const now = useClock();
  // Latest session/turn state for async callbacks and the unmount cost guard below.
  const sessionRef = useRef<string | null>(null);
  const runningRef = useRef(running);
  sessionRef.current = sessionId;
  runningRef.current = running;

  // Shell output history (fenced code blocks the playground streams back in replies). This is
  // INFERRED from the transcript, not read from the sandbox — it only shows what the agent chose
  // to echo. `ownRuns` below is the real thing: commands the owner ran, with their actual exit
  // codes. Both are shown, oldest-transcript-first, so the panel reads as one session log.
  const blocks: Array<{ id: string; body: string }> = [];
  const fence = /```[\w-]*\n?([\s\S]*?)```/g;
  for (const m of messages) {
    if (m.role !== "assistant") continue;
    let match: RegExpExecArray | null;
    fence.lastIndex = 0;
    while ((match = fence.exec(m.text || "")) !== null) {
      const body = (match[1] ?? "").replace(/\s+$/, "");
      if (body.trim().length > 0) blocks.push({ id: `${m.id}:${blocks.length}`, body });
    }
  }

  // The owner's OWN commands, run against the same AgentCore sandbox the agent uses. Before
  // this the Terminal panel was read-only and scraped from chat, so the sandbox could not be
  // inspected or steered at all — you had to ask the agent to echo something.
  const [ownRuns, setOwnRuns] = useState<
    ReadonlyArray<{
      readonly id: string;
      readonly command: string;
      readonly out: string | null;
      readonly failed: boolean;
    }>
  >([]);
  const [cmd, setCmd] = useState("");
  const [cmdBusy, setCmdBusy] = useState(false);
  const termScrollRef = useRef<HTMLDivElement | null>(null);

  const submitCommand = useCallback(async () => {
    const command = cmd.trim();
    if (!command || cmdBusy) return;
    const id = `own:${Date.now()}`;
    setCmd("");
    setCmdBusy(true);
    // Echo immediately with `out: null` so the prompt feels live while AgentCore starts the
    // command — a cold sandbox can take a few seconds.
    setOwnRuns((prev) => [...prev, { id, command, out: null, failed: false }]);
    const res = await runAgentCommand(agent.id, command).catch(() => null);
    setOwnRuns((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        if (!res) {
          // postJsonGated swallows the body on a non-2xx; a 402 has already opened the
          // upgrade modal, so say something true rather than inventing a cause.
          return { ...r, out: "Could not reach the agent's computer.", failed: true };
        }
        const parts = [res.stdout, res.stderr].filter((s) => s && s.length > 0);
        const tail =
          res.timedOut === true
            ? "^ timed out"
            : res.exitCode !== null && res.exitCode !== 0
              ? `^ exit ${res.exitCode}`
              : res.error
                ? res.error
                : "";
        const body = [...parts, tail].filter((s) => s.length > 0).join("\n");
        return {
          ...r,
          out: body.length > 0 ? body : "(no output)",
          failed: !res.ok,
        };
      }),
    );
    setCmdBusy(false);
  }, [agent.id, cmd, cmdBusy]);

  // Keep the newest output in view as it lands.
  useEffect(() => {
    const el = termScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [ownRuns, active.view]);

  // The apps on the agent's home screen: the ones it may use (allowedApps), else none.
  const apps = useMemo(
    () =>
      (agent.allowedApps ?? []).slice(0, 10).map((slug) => {
        const { logoUrl, appName } = resolveContextToolIcon(slug);
        return { slug, logoUrl, label: appName ?? humanizeTool(slug) };
      }),
    [agent.allowedApps],
  );

  /** Watch the agent's own in-progress browser if it has one. Never creates a session. */
  const attachToAgent = useCallback(async () => {
    const live = await agentBrowserLive(agent.id);
    if (live?.live && live.liveUrl) {
      setLiveUrl(live.liveUrl);
      setSessionId(live.sessionId ?? null);
      setErr(null);
      setReloadKey((k) => k + 1);
      return true;
    }
    return false;
  }, [agent.id]);

  /** Start (or reuse) the shared browser — the one the agent also drives, so a login you do
   * here is immediately the agent's. `fresh` forces a brand-new session. */
  const startBrowser = useCallback(
    async (opts?: { fresh?: boolean }) => {
      setBusy(true);
      setErr(null);
      if (!opts?.fresh && (await attachToAgent())) {
        setBusy(false);
        return;
      }
      const res = await openBrowserUse({
        width: LIVE_WIDTH,
        height: SCREEN_HEIGHT,
        fresh: opts?.fresh === true,
      });
      setBusy(false);
      if (!res?.ok || !res.liveUrl || !res.sessionId) {
        setErr(res?.error || "Couldn't start the browser. Try again.");
        return;
      }
      setLiveUrl(res.liveUrl);
      setSessionId(res.sessionId);
      setReloadKey((k) => k + 1);
    },
    [attachToAgent],
  );

  // On open, and whenever the agent starts browsing, show its live browser if it has one.
  useEffect(() => {
    if (!liveUrl && !busy) void attachToAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseUrl]);

  // Taking over needs a screen to drive: start one if the agent wasn't browsing.
  useEffect(() => {
    if (userHasControl && !liveUrl && !busy) void startBrowser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userHasControl]);

  // Poll the embedded session's liveness; if it dies, drop back to the home screen.
  useEffect(() => {
    if (!liveUrl || !sessionId || busy) return;
    let alive = true;
    const polled = sessionId;
    const timer = setInterval(async () => {
      const ok = await browserUseStatus(polled).catch(() => true);
      // Only drop the session this check was about — a restart may have replaced it meanwhile.
      if (!alive || ok || sessionRef.current !== polled) return;
      setLiveUrl(null);
      setSessionId(null);
    }, 20000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [liveUrl, sessionId, busy]);

  // COST GUARD: whenever the pane goes away (closed, or you navigate off the page) and the agent
  // isn't mid-turn, stop the browser session so it doesn't keep billing. Logins persist in the
  // saved profile, so the agent restores them on its next use.
  useEffect(
    () => () => {
      if (sessionRef.current && !runningRef.current) void stopBrowserUse(sessionRef.current);
    },
    [],
  );
  const closePane = onClose;

  const addTab = () => {
    const id = Math.max(...tabs.map((t) => t.id)) + 1;
    setTabs((prev) => [...prev, { id, view: active.view === "browser" ? "terminal" : "browser" }]);
    setActiveId(id);
  };
  const closeTab = (id: number) => {
    if (tabs.length === 1) return closePane();
    const rest = tabs.filter((t) => t.id !== id);
    setTabs(rest);
    if (id === activeId) setActiveId(rest[rest.length - 1]!.id);
  };

  const showBrowser = active.view === "browser" && !!liveUrl;

  return (
    <div className="relative flex min-h-0 w-full flex-col border-l border-border bg-background max-md:absolute max-md:inset-0 max-md:z-30 md:w-[54%] md:min-w-[480px] md:max-w-[960px]">
      {/* Tab strip */}
      <div className="flex h-12 shrink-0 items-center gap-1 px-2">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
          {tabs.map((t, i) => {
            const on = t.id === active.id;
            return (
              <div
                key={t.id}
                className={`group flex h-8 min-w-0 max-w-[220px] items-center gap-1.5 rounded-lg pl-2.5 pr-1 text-[13px] transition ${
                  on
                    ? "bg-card text-foreground shadow-sm ring-1 ring-border"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActiveId(t.id)}
                  className="flex min-w-0 items-center gap-1.5 focus-visible:outline-none"
                >
                  <MonitorIcon className="size-3.5 shrink-0" />
                  <span className="truncate">
                    {name}&rsquo;s computer{tabs.length > 1 ? ` ${i + 1}` : ""}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => closeTab(t.id)}
                  aria-label={`Close ${name}'s computer${tabs.length > 1 ? ` ${i + 1}` : ""}`}
                  className="flex size-5 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-70 transition hover:bg-accent hover:opacity-100"
                >
                  <XIcon className="size-3" />
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addTab}
            aria-label="Open another view of the computer"
            title="Open another view"
            className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <PlusIcon className="size-4" />
          </button>
        </div>
        {showBrowser ? (
          <button
            type="button"
            onClick={() => void startBrowser({ fresh: true })}
            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
            aria-label="Restart the browser"
            title="Restart the browser"
          >
            <RotateCwIcon className="size-4" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={closePane}
          className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
          aria-label="Hide the computer"
          title="Hide the computer"
        >
          <PanelRightIcon className="size-4" />
        </button>
      </div>

      {/* Stage + control pill */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4 pb-5 pt-1">
        <div
          className="flex w-full max-w-[880px] flex-col items-center gap-3 rounded-[28px] p-[4%] shadow-sm [--stage-hi:86%] [--stage-lo:72%] [--stage-mid:92%] dark:[--stage-hi:46%] dark:[--stage-lo:30%] dark:[--stage-mid:40%]"
          style={{
            // The agent's colour mixed into the active theme's own surfaces, so it sits inside
            // any preset (MorrowOS, Grove, Ocean, Ember, Iris): vivid on light, deep on dark.
            background: `linear-gradient(160deg, color-mix(in oklab, ${palette.fill} var(--stage-hi), var(--card)), color-mix(in oklab, ${palette.fill} var(--stage-mid), var(--background)) 55%, color-mix(in oklab, ${palette.fill} var(--stage-lo), var(--background)))`,
          }}
        >
          {/* The screen */}
          <div className="relative aspect-[640/417] w-full overflow-hidden rounded-xl bg-card shadow-[0_12px_40px_rgb(0_0_0/0.18)] ring-1 ring-black/10">
            {active.view === "terminal" ? (
              <div className="absolute inset-0 flex flex-col bg-[var(--terminal-background)] font-mono text-[12px] leading-relaxed text-[var(--terminal-foreground)]">
                <div ref={termScrollRef} className="flex-1 overflow-y-auto px-4 py-3">
                  {blocks.length === 0 && ownRuns.length === 0 ? (
                    <div className="opacity-50">
                      {name}&rsquo;s computer. Run a command below, or ask {name} to — you share the
                      same machine, so its files are here too.
                    </div>
                  ) : null}
                  {blocks.map((b) => (
                    <pre key={b.id} className="mb-3 whitespace-pre-wrap break-words">
                      <span className="select-none opacity-40">$ </span>
                      {b.body}
                    </pre>
                  ))}
                  {ownRuns.map((r) => (
                    <pre key={r.id} className="mb-3 whitespace-pre-wrap break-words last:mb-0">
                      <span className="select-none opacity-40">$ </span>
                      {r.command}
                      {r.out === null ? (
                        <span className="opacity-50">{"\n…"}</span>
                      ) : (
                        <span className={r.failed ? "text-red-400" : undefined}>
                          {"\n"}
                          {r.out}
                        </span>
                      )}
                    </pre>
                  ))}
                </div>
                {/* The prompt. The agent's computer was previously unreachable from the UI —
                    this is the only way to inspect or steer the sandbox directly. */}
                <form
                  className="flex items-center gap-2 border-t border-white/10 px-4 py-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitCommand();
                  }}
                >
                  <span aria-hidden className="select-none opacity-40">
                    $
                  </span>
                  <input
                    value={cmd}
                    onChange={(e) => setCmd(e.target.value)}
                    disabled={cmdBusy}
                    spellCheck={false}
                    autoComplete="off"
                    aria-label={`Run a command on ${name}'s computer`}
                    placeholder={cmdBusy ? "running…" : "ls /mnt/session"}
                    className="min-w-0 flex-1 bg-transparent font-mono text-[12px] text-[var(--terminal-foreground)] placeholder:opacity-40 focus:outline-none disabled:opacity-60"
                  />
                  {cmdBusy ? <Loader2Icon className="size-3.5 shrink-0 animate-spin" /> : null}
                </form>
              </div>
            ) : showBrowser ? (
              <ScaledLiveView
                key={`${liveUrl}:${reloadKey}`}
                src={liveUrl ?? ""}
                title={`${name}'s browser`}
                interactive={userHasControl}
              />
            ) : (
              // Home screen — the agent's computer at rest.
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1 overflow-hidden px-6 text-center"
                style={{ background: `color-mix(in oklab, ${palette.fill} 10%, var(--card))` }}
              >
                {busy ? (
                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    <Loader2Icon className="size-5 animate-spin" />
                    Starting {name}&rsquo;s browser…
                  </div>
                ) : (
                  <>
                    <div className="relative mb-1">
                      <div className="absolute bottom-full left-1/2 mb-1 w-max max-w-[220px] -translate-x-1/2 rounded-xl bg-card px-3 py-1.5 text-[11px] leading-snug text-foreground shadow-sm ring-1 ring-border">
                        {err ??
                          "This is my computer. Watch me work, or take control when you need to."}
                      </div>
                      <span className="agent-mascot-bob block">
                        <AgentMascot agent={agent} size={52} />
                      </span>
                    </div>
                    <div className="text-4xl font-light tabular-nums tracking-tight text-foreground">
                      {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                    <div className="text-sm text-muted-foreground">Welcome back, {name}</div>
                    {apps.length > 0 ? (
                      <div className="mt-3 grid grid-cols-5 gap-x-4 gap-y-2">
                        {apps.map((a) => (
                          <div key={a.slug} className="flex w-14 flex-col items-center gap-1">
                            <span className="flex size-9 items-center justify-center rounded-xl bg-card shadow-sm ring-1 ring-border">
                              {a.logoUrl ? (
                                <img src={a.logoUrl} alt="" className="size-5 object-contain" />
                              ) : (
                                <WrenchIcon className="size-4 text-muted-foreground" />
                              )}
                            </span>
                            <span className="w-full truncate text-[10px] text-muted-foreground">
                              {a.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Dock */}
          <div className="flex items-center gap-1.5 rounded-2xl bg-white/25 p-1.5 backdrop-blur-sm dark:bg-black/20">
            {(
              [
                { view: "browser", Icon: GlobeIcon, label: "Browser" },
                { view: "terminal", Icon: TerminalIcon, label: "Terminal" },
              ] as const
            ).map(({ view, Icon, label }) => {
              const on = active.view === view;
              return (
                <button
                  key={view}
                  type="button"
                  onClick={() => {
                    setView(view);
                    if (view === "browser" && !liveUrl && !busy) void startBrowser();
                  }}
                  aria-label={label}
                  aria-pressed={on}
                  title={view === "browser" && !liveUrl ? "Open the browser" : label}
                  className={`relative flex size-9 items-center justify-center rounded-xl transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
                    view === "terminal"
                      ? "bg-[var(--terminal-background)] text-[var(--terminal-foreground)]"
                      : "bg-card text-foreground"
                  } ${on ? "shadow-md" : "opacity-80 hover:opacity-100"}`}
                >
                  <Icon className="size-[18px]" />
                  {on ? (
                    <span className="absolute -bottom-1 left-1/2 size-1 -translate-x-1/2 rounded-full bg-white/90" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        {/* Who is driving */}
        <div className="flex items-center gap-2 rounded-full bg-card py-1 pl-3.5 pr-1 text-xs shadow-sm ring-1 ring-border">
          <span className="font-medium text-foreground">
            {userHasControl ? "You have control" : `${name} has control`}
          </span>
          <button
            type="button"
            onClick={userHasControl ? onGiveBack : onTakeOver}
            className="rounded-full bg-foreground px-3 py-1 font-medium text-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {userHasControl ? "Give back" : "Take over"}
          </button>
        </div>
      </div>
    </div>
  );
}

const D_INPUT =
  "w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground " +
  "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

function ProfileDrawer(props: {
  agent: Agent;
  draft: Draft;
  setDraft: (d: Draft) => void;
  runs: readonly AgentRun[];
  saving: boolean;
  busy: boolean;
  newTag: string;
  setNewTag: (v: string) => void;
  onClose: () => void;
  onSave: () => void;
  onToggleStatus: () => void;
  onRun: () => void;
  onDelete: () => void;
  onAddTag: () => void;
  onRemoveTag: (handle: string, channelScope: string) => void;
}) {
  const { agent, draft, setDraft, runs } = props;
  const connected = useConnectedApps(true);
  const values: AgentFormValues = {
    avatar: draft.avatar,
    name: draft.name,
    role: draft.role,
    soul: draft.soul,
    purpose: draft.purpose,
    autonomy: draft.autonomy,
    allowedApps: draft.allowedApps,
    caps: draft.caps,
    keepWorking: draft.keepWorking,
    dailyBudgetMinutes: draft.dailyBudgetMinutes,
  };
  const patch = (p: Partial<AgentFormValues>) => setDraft({ ...draft, ...p });
  const activityContent = (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <div className="text-[13px] font-semibold text-foreground">Reach it over comms</div>
        <div className="-mt-1 text-xs text-muted-foreground">
          Tag handles it answers to (WhatsApp, etc.).
        </div>
        <div className="flex flex-wrap gap-1.5">
          {agent.tags.map((t) => (
            <span
              key={`${t.handle}:${t.channelScope}`}
              className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1 text-xs text-secondary-foreground"
            >
              @{t.handle}
              <button
                type="button"
                onClick={() => props.onRemoveTag(t.handle, t.channelScope)}
                aria-label="Remove"
              >
                <XIcon className="size-3" />
              </button>
            </span>
          ))}
          {agent.tags.length === 0 ? (
            <span className="text-xs text-muted-foreground">No tags yet.</span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <input
            className={D_INPUT}
            placeholder="new tag (without @)"
            value={props.newTag}
            onChange={(e) => props.setNewTag(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") props.onAddTag();
            }}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={props.onAddTag}
            disabled={!props.newTag.trim()}
          >
            <PlusIcon className="size-4" />
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="text-[13px] font-semibold text-foreground">Run history ({runs.length})</div>
        {runs.length === 0 ? (
          <div className="text-xs text-muted-foreground">Nothing yet — it hasn&rsquo;t run.</div>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {runs.slice(0, 40).map((r) => (
              <li
                key={r.id}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-2.5 py-1.5"
              >
                <div className="flex min-w-0 items-center gap-1.5">
                  <Badge variant="outline" size="sm">
                    {SOURCE_LABEL[r.source] ?? r.source}
                  </Badge>
                  <span className="truncate text-xs text-foreground">{r.detail ?? "run"}</span>
                </div>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {new Date(r.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
  return (
    <Drawer onClose={props.onClose} size="xl">
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Header */}
        <div
          className="relative flex shrink-0 flex-col items-center gap-2 px-4 pb-4 pt-6"
          style={{
            background: `linear-gradient(180deg, color-mix(in oklab, ${presetMascotBody(draft.avatar) ?? "var(--muted)"} 16%, var(--background)), var(--background))`,
          }}
        >
          <button
            type="button"
            onClick={props.onClose}
            className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-full text-muted-foreground transition hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <XIcon className="size-5" />
          </button>
          <AgentAvatar name={draft.name || agent.name} avatar={draft.avatar} size={72} round />
          <div className="text-center">
            <div className="text-base font-semibold text-foreground">
              {draft.name || agent.name}
            </div>
            <div className="text-sm text-muted-foreground">{draft.role || "Agent"}</div>
            {agent.tags.length > 0 ? (
              <div className="mt-1.5 text-xs text-muted-foreground">
                Message it from your connected apps with{" "}
                {agent.tags.map((t, i) => (
                  <span key={`${t.handle}:${t.channelScope}`}>
                    {i > 0 ? ", " : ""}
                    <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-foreground">
                      @{t.handle}
                    </span>
                  </span>
                ))}
              </div>
            ) : null}
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={props.onToggleStatus}
              disabled={props.busy}
            >
              {agent.status === "paused" ? "Resume" : "Pause"}
            </Button>
            <Button
              size="sm"
              onClick={props.onRun}
              disabled={props.busy || agent.status !== "active"}
            >
              <PlayIcon className="size-4" /> Run now
            </Button>
          </div>
        </div>

        {/* Tabbed form — shared with the create dialog (Identity/Tools/Autonomy + Activity) */}
        <div className="flex min-h-0 flex-1 flex-col p-5">
          <AgentTabs
            values={values}
            patch={patch}
            connected={connected}
            extraTabs={[
              { id: "activity", label: "Activity", Icon: ListIcon, content: activityContent },
            ]}
          />
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border bg-background px-4 py-3">
          <Button
            variant="destructive-outline"
            size="sm"
            onClick={props.onDelete}
            disabled={props.busy}
          >
            <Trash2Icon className="size-4" /> Delete
          </Button>
          <Button onClick={props.onSave} disabled={props.saving}>
            {props.saving ? <Loader2Icon className="size-4 animate-spin" /> : null} Save changes
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
